import React, { createContext, useContext, useState, useEffect } from 'react';
import { Property, Booking, Notification, Payment, Message, SavedProperty, User, UserRole, ReferralCampaign, WithdrawalRequest, ApplicationForm, Landlord, AuditLog, InternalNote } from '../types';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from './AuthContext';

interface DataContextType {
  properties: Property[];
  bookings: Booking[];
  bookingsError: string | null;
  notifications: Notification[];
  payments: Payment[];
  messages: Message[];
  savedProperties: string[];
  users: User[]; // Admin use
  referralCampaigns: ReferralCampaign[];
  withdrawalRequests: WithdrawalRequest[];
  applicationForms: ApplicationForm[];
  landlords: Landlord[];
  auditLogs: AuditLog[];
  internalNotes: InternalNote[];

  addProperty: (property: Omit<Property, 'id' | 'status'>) => Promise<{ data?: any, error: any }>;
  updateProperty: (id: string, property: Partial<Property>) => Promise<{ error: any }>;
  deleteProperty: (id: string, hardDelete?: boolean) => Promise<void>;

  addBooking: (booking: Omit<Booking, 'id' | 'status' | 'date'>) => Promise<boolean>;
  confirmBooking: (bookingId: string) => Promise<boolean>;
  rejectBooking: (bookingId: string) => Promise<boolean>; // New function
  setBookingStatus: (id: string, status: Booking['status']) => Promise<void>; // Direct status update
  updateBooking: (id: string, updates: Partial<Booking>) => Promise<boolean>;
  reassignPropertyAgent: (propertyId: string, agentId: string) => Promise<boolean>;

  addNotification: (notification: Omit<Notification, 'id' | 'date' | 'read'>) => void;
  markNotificationRead: (id: string) => void;

  sendMessage: (receiverId: string, content: string, propertyId?: string) => Promise<void>;
  markMessagesAsRead: (senderId: string) => Promise<void>;
  toggleSavedProperty: (propertyId: string) => Promise<void>;
  payPayment: (paymentId: string) => Promise<void>;

  // User Management
  updateUser: (id: string, updates: Partial<User>) => Promise<void>;
  deleteUser: (id: string, hardDelete?: boolean) => Promise<{ error: any | null }>;

  // Landlord Management
  addLandlord: (landlord: Omit<Landlord, 'id' | 'createdAt' | 'updatedAt'>) => Promise<{ data?: any, error: any }>;
  updateLandlord: (id: string, updates: Partial<Landlord>) => Promise<{ error: any }>;
  deleteLandlord: (id: string, hardDelete?: boolean) => Promise<void>;

  // Internal Notes
  addNote: (note: Omit<InternalNote, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  deleteNote: (id: string) => Promise<void>;

  // Audit Logging
  logAction: (action: string, targetId: string, targetType: string, payload?: any) => Promise<void>;

  // Referral Management
  addReferralCampaign: (campaign: Omit<ReferralCampaign, 'id'>) => void;
  toggleReferralCampaign: (id: string) => void;
  validateReferralCode: (code: string) => User | undefined;
  requestWithdrawal: (details: Omit<WithdrawalRequest, 'id' | 'status' | 'createdAt'>) => Promise<boolean>;
  updateWithdrawalStatus: (id: string, status: 'PAID' | 'REJECTED') => Promise<void>;

  // Forms Management
  addApplicationForm: (form: Omit<ApplicationForm, 'id' | 'createdAt'>) => Promise<boolean>;
  updateApplicationForm: (id: string, updates: Partial<ApplicationForm>) => Promise<boolean>;
  toggleApplicationForm: (id: string) => Promise<void>;

  refreshData: () => void;

  // Helpers
  getUserLastActivity: (userId: string) => string;
  getUserSubscription: (userId: string) => Payment | undefined;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();

  const [properties, setProperties] = useState<Property[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [bookingsError, setBookingsError] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [savedProperties, setSavedProperties] = useState<string[]>([]);

  // Admin State
  const [users, setUsers] = useState<User[]>([]);
  const [referralCampaigns, setReferralCampaigns] = useState<ReferralCampaign[]>([]);
  const [withdrawalRequests, setWithdrawalRequests] = useState<WithdrawalRequest[]>([]);
  const [applicationForms, setApplicationForms] = useState<ApplicationForm[]>([]);
  const [landlords, setLandlords] = useState<Landlord[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [internalNotes, setInternalNotes] = useState<InternalNote[]>([]);

  // Generate a mock referral code if one doesn't exist (Frontend fallback)
  const generateReferralCode = (u: User) => {
    if (u.referralCode) return u.referralCode;
    const namePart = u.name.substring(0, 3).toUpperCase().replace(/[^A-Z]/g, 'X');
    const idPart = u.id.substring(0, 4).toUpperCase();
    return `${namePart}${idPart}`;
  };

  // Fetch initial data
  const fetchAllData = async () => {
    // 1. Properties
    const query = supabase.from('properties').select('*');
    if (user?.role !== UserRole.ADMIN) {
      query.is('deleted_at', null); // Soft delete filter for non-admins
    }
    const { data: propData } = await query.order('created_at', { ascending: false });
    if (propData) {
      const mappedProperties: Property[] = propData.map((p: any) => ({
        id: p.id,
        title: p.title,
        location: p.location,
        locationName: p.location_name, // Map Location Name
        latitude: p.latitude, // Map Lat
        longitude: p.longitude, // Map Lng
        price: p.price,
        type: p.type,
        bedrooms: p.bedrooms,
        bathrooms: p.bathrooms,
        image: p.image,
        images: p.images || [p.image],
        agentId: p.agent_id,
        coAgentId: p.co_agent_id,
        status: p.status,
        description: p.description,
        amenities: p.amenities,
        rooms: p.rooms,
        beds: p.beds, // Map beds
        landlordId: p.landlord_id,
        deletedAt: p.deleted_at,
        deletedBy: p.deleted_by
      }));
      setProperties(mappedProperties);
    }

    // 2. Users (Admin/Demo view)
    const userQuery = supabase.from('profiles').select('*');
    if (user?.role !== UserRole.ADMIN) {
      userQuery.is('deleted_at', null);
    }
    const { data: userData } = await userQuery;
    if (userData) {
      const mappedUsers: User[] = userData.map((u: any) => ({
        id: u.id,
        name: u.name,
        email: u.email,
        role: u.role,
        status: u.status,
        avatar: u.avatar_url,
        phoneNumber: u.phone_number,
        institution: u.institution,
        agencyName: u.agency_name,
        propertyCount: u.property_count,
        licenseNumber: u.license_number,
        referralCode: u.referral_code,
        referralBalance: u.referral_balance || 0,
        lifetimeEarnings: u.lifetime_earnings || 0,
        referralCount: u.referral_count || 0,
        isTrusted: u.is_trusted || false, // Map trusted status
        deletedAt: u.deleted_at,
        deletedBy: u.deleted_by
      }));

      // Fallback generation if DB trigger failed or old data
      const usersWithCodes = mappedUsers.map(u => ({
        ...u,
        referralCode: u.referralCode || generateReferralCode(u)
      }));
      setUsers(usersWithCodes);
    }

    // 3. Referral Campaigns (Public)
    const { data: campaignData } = await supabase.from('referral_campaigns').select('*').order('created_at', { ascending: false });
    if (campaignData) {
      setReferralCampaigns(campaignData.map((c: any) => ({
        id: c.id,
        name: c.name,
        benefit: c.benefit,
        rewardAmount: c.reward_amount,
        thresholdAmount: c.threshold_amount,
        isActive: c.is_active,
        startDate: c.start_date,
        endDate: c.end_date
      })));
    }

    // 4. Withdrawal Requests (Admin & User)
    const { data: withdrawalData } = await supabase.from('withdrawal_requests').select('*').order('created_at', { ascending: false });
    if (withdrawalData) {
      setWithdrawalRequests(withdrawalData.map((w: any) => ({
        id: w.id,
        userId: w.user_id,
        amount: w.amount,
        status: w.status,
        createdAt: w.created_at,
        bankName: w.bank_name,
        accountNumber: w.account_number,
        accountHolderName: w.account_holder_name,
        branchCode: w.branch_code,
        idNumber: w.id_number,
        email: w.email
      })));
    }

    // 5. Application Forms
    const { data: formData } = await supabase.from('application_forms').select('*').order('created_at', { ascending: false });
    if (formData) {
      setApplicationForms(formData.map((f: any) => ({
        id: f.id,
        agentId: f.agent_id,
        title: f.title,
        type: f.type,
        description: f.description,
        isActive: f.is_active,
        createdAt: f.created_at,
        fields: f.fields || [] // Handle new column
      })));
    }

    if (!user) return;

    // 5. Bookings
    setBookingsError(null);
    const { data: bookData, error: bookError } = await supabase.from('bookings').select('*');
    console.log('[DataContext] Fetching bookings...', { count: bookData?.length, error: bookError });
    if (bookError) {
      console.error('[DataContext] Booking fetch error:', bookError);
      setBookingsError(bookError.message);
    }
    if (bookData) {
      setBookings(bookData.map((b: any) => ({
        id: b.id,
        propertyId: b.property_id,
        studentId: b.student_id,
        roomId: b.room_id,
        bedId: b.bed_id,
        status: b.status,
        date: b.date,
        referralCode: b.referral_code
      })));
      console.log('[DataContext] Bookings loaded:', bookData.map((b: any) => ({ id: b.id, student_id: b.student_id, status: b.status })));
    } else if (!bookError) {
      setBookings([]);
    }

    // 6. Payments
    const { data: payData } = await supabase.from('payments').select('*');
    if (payData) {
      setPayments(payData.map((p: any) => ({
        id: p.id,
        bookingId: p.booking_id,
        studentId: p.student_id,
        amount: p.amount,
        status: p.status,
        title: p.title,
        dueDate: p.due_date,
        paidAt: p.paid_at
      })));
    }

    // 7. Messages
    let msgQuery = supabase.from('messages').select('*').order('created_at', { ascending: true });
    if (user.role !== UserRole.ADMIN) {
      msgQuery = msgQuery.or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`);
    }
    const { data: msgData } = await msgQuery;
    if (msgData) {
      setMessages(msgData.map((m: any) => ({
        id: m.id,
        senderId: m.sender_id,
        receiverId: m.receiver_id,
        propertyId: m.property_id,
        content: m.content,
        isRead: m.is_read,
        createdAt: m.created_at
      })));
    }

    // 8. Saved Properties
    const { data: savedData } = await supabase.from('saved_properties').select('property_id').eq('user_id', user.id);
    if (savedData) {
      setSavedProperties(savedData.map((s: any) => s.property_id));
    }

    // 9. Notifications (Persistent)
    const { data: notifData } = await supabase.from('notifications').select('*').eq('user_id', user.id).order('created_at', { ascending: false });
    if (notifData) {
      setNotifications(notifData.map((n: any) => ({
        id: n.id,
        userId: n.user_id,
        message: n.message,
        type: n.type,
        date: n.created_at,
        read: n.read
      })));
    }

    // 10. Landlords
    const landlordQuery = supabase.from('landlords').select('*');
    if (user.role !== UserRole.ADMIN) {
      landlordQuery.is('deleted_at', null);
    }
    const { data: lData } = await landlordQuery.order('created_at', { ascending: false });
    if (lData) {
      setLandlords(lData.map((l: any) => ({
        id: l.id,
        agentId: l.agent_id,
        fullName: l.full_name,
        phoneNumber: l.phone_number,
        email: l.email,
        physicalAddress: l.physical_address,
        createdAt: l.created_at,
        updatedAt: l.updated_at,
        deletedAt: l.deleted_at
      })));
    }

    // 11. Internal Notes
    const notesQuery = supabase.from('internal_notes').select('*');
    if (user.role !== UserRole.ADMIN) {
      notesQuery.is('deleted_at', null);
    }
    const { data: nData } = await notesQuery.order('created_at', { ascending: false });
    if (nData) {
      setInternalNotes(nData.map((n: any) => ({
        id: n.id,
        userId: n.user_id,
        targetId: n.target_id,
        targetType: n.target_type,
        content: n.content,
        createdAt: n.created_at,
        updatedAt: n.updated_at,
        deletedAt: n.deleted_at
      })));
    }

    // 12. Audit Logs (Admin Only)
    if (user.role === UserRole.ADMIN) {
      const { data: aData } = await supabase.from('audit_logs').select('*').order('created_at', { ascending: false });
      if (aData) {
        setAuditLogs(aData.map((a: any) => ({
          id: a.id,
          userId: a.user_id,
          action: a.action,
          targetId: a.target_id,
          targetType: a.target_type,
          payload: a.payload,
          createdAt: a.created_at
        })));
      }
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [user]);

  const refreshData = () => {
    fetchAllData();
  };

  useEffect(() => {
    fetchAllData();
  }, [user]);


  // --- Helpers ---
  const getUserLastActivity = (userId: string) => {
    const userBookings = bookings.filter(b => b.studentId === userId);
    const userMessages = messages.filter(m => m.senderId === userId);

    let lastDate = new Date(0);
    userBookings.forEach(b => { const date = new Date(b.date); if (date > lastDate) lastDate = date; });
    userMessages.forEach(m => { const date = new Date(m.createdAt); if (date > lastDate) lastDate = date; });

    if (lastDate.getTime() === 0) return 'No recent activity';
    return lastDate.toLocaleDateString() + ' ' + lastDate.toLocaleTimeString();
  };

  const getUserSubscription = (userId: string) => {
    return payments.find(p =>
      p.studentId === userId &&
      (p.title.includes('Plan') || p.title.includes('Subscription')) &&
      p.status === 'COMPLETED'
    );
  };

  // --- Property Actions ---
  const addProperty = async (newProp: Omit<Property, 'id' | 'status'>) => {
    try {
      // Logic: If user is Trusted (Agent/Admin), default to AVAILABLE, else PENDING
      const currentUser = users.find(u => u.id === newProp.agentId);
      const isTrusted = currentUser?.isTrusted || currentUser?.role === UserRole.ADMIN || false;
      const initialStatus = isTrusted ? 'AVAILABLE' : 'PENDING';

      const { data, error } = await supabase.from('properties').insert([{
        title: newProp.title,
        location: newProp.location,
        location_name: newProp.locationName, // Add Location Name
        latitude: newProp.latitude,
        longitude: newProp.longitude,
        price: newProp.price,
        type: newProp.type,
        bedrooms: newProp.bedrooms,
        bathrooms: newProp.bathrooms,
        image: newProp.image, // Primary image
        images: newProp.images, // All images
        agent_id: newProp.agentId || null,
        co_agent_id: newProp.coAgentId || null,
        landlord_id: newProp.landlordId || null,
        // Add landlord_id
        status: initialStatus,
        description: newProp.description,
        amenities: newProp.amenities,
        rooms: newProp.rooms, // Include rooms
        beds: newProp.beds // Include beds
      }]).select();

      if (error) {
        console.error("Supabase Insert Error:", error);
        return { error };
      }

      if (data) {
        fetchAllData();

        // Notify all students about the new property
        try {
          const studentIds = users.filter(u => u.role === UserRole.STUDENT).map(u => u.id);
          const notificationPayloads = studentIds.map(sid => ({
            user_id: sid,
            message: `New property listed: ${newProp.title} in ${newProp.location}`,
            type: 'info',
            read: false
          }));

          if (notificationPayloads.length > 0) {
            await supabase.from('notifications').insert(notificationPayloads);
          }
        } catch (notifErr) {
          console.error("Failed to send global notifications:", notifErr);
        }
      }
      return { data: data[0], error: null }; // Return the created property
    } catch (err) {
      console.error("Failed to save property", err);
      return { error: err };
    }
  };

  const updateProperty = async (id: string, updates: Partial<Property>) => {
    // Optimistic Update
    setProperties(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));

    // Construct database payload (convert camelCase to snake_case for DB)
    const payload: any = {};
    if (updates.title !== undefined) payload.title = updates.title;
    if (updates.price !== undefined) payload.price = updates.price;
    if (updates.status !== undefined) payload.status = updates.status;
    if (updates.images !== undefined) payload.images = updates.images;
    if (updates.image !== undefined) payload.image = updates.image;
    if (updates.description !== undefined) payload.description = updates.description;
    if (updates.location !== undefined) payload.location = updates.location;
    if (updates.locationName !== undefined) payload.location_name = updates.locationName;
    if (updates.latitude !== undefined) payload.latitude = updates.latitude;
    if (updates.longitude !== undefined) payload.longitude = updates.longitude;
    if (updates.bedrooms !== undefined) payload.bedrooms = updates.bedrooms;
    if (updates.bathrooms !== undefined) payload.bathrooms = updates.bathrooms;
    if (updates.type !== undefined) payload.type = updates.type;
    if (updates.amenities !== undefined) payload.amenities = updates.amenities;
    if (updates.rooms !== undefined) payload.rooms = updates.rooms;
    if (updates.beds !== undefined) payload.beds = updates.beds;
    if (updates.agentId !== undefined) payload.agent_id = updates.agentId || null;
    if (updates.coAgentId !== undefined) payload.co_agent_id = updates.coAgentId || null;
    if (updates.landlordId !== undefined) payload.landlord_id = updates.landlordId || null;

    const { error } = await supabase.from('properties').update(payload).eq('id', id);

    if (error) {
      console.error("Error updating property:", error);
    } else {
      // Refresh to ensure we are in sync with server
      fetchAllData();
    }
    return { error };
  };

  const deleteProperty = async (id: string, hardDelete: boolean = false) => {
    if (hardDelete && user?.role === UserRole.ADMIN) {
      setProperties(prev => prev.filter(p => p.id !== id));
      await supabase.from('properties').delete().eq('id', id);
    } else {
      // Soft Delete
      const deletionData = {
        deleted_at: new Date().toISOString(),
        deleted_by: user?.id
      };
      setProperties(prev => prev.map(p => p.id === id ? { ...p, deletedAt: deletionData.deleted_at, deletedBy: deletionData.deleted_by } : p));
      await supabase.from('properties').update(deletionData).eq('id', id);

      // Log Action
      logAction('PROPERTY_SOFT_DELETE', id, 'property', { id });
    }
  };

  // --- Booking Actions ---
  const addBooking = async (newBooking: Omit<Booking, 'id' | 'status' | 'date'>) => {
    // 1. Optimistic Update
    const tempId = `temp-${Date.now()}`;
    setBookings(prev => [...prev, {
      id: tempId,
      propertyId: newBooking.propertyId,
      studentId: newBooking.studentId,
      roomId: newBooking.roomId,
      bedId: newBooking.bedId,
      status: 'PENDING',
      date: new Date().toISOString(),
      referralCode: newBooking.referralCode
    }]);

    // 2. Construct DB Payload
    const bookingPayload: any = {
      property_id: newBooking.propertyId,
      student_id: newBooking.studentId,
      status: 'PENDING',
      date: new Date().toISOString(),
    };

    if (newBooking.roomId) bookingPayload.room_id = newBooking.roomId;
    if (newBooking.bedId) bookingPayload.bed_id = newBooking.bedId;
    if (newBooking.referralCode) bookingPayload.referral_code = newBooking.referralCode;

    const { data, error } = await supabase.from('bookings').insert([bookingPayload]).select();

    if (error) {
      console.error("Booking Error:", error);
      // Rollback
      setBookings(prev => prev.filter(b => b.id !== tempId));
      return false;
    }

    if (data) {
      setBookings(prev => prev.map(b => b.id === tempId ? { ...b, id: data[0].id } : b));
      addNotification({
        userId: newBooking.studentId,
        message: 'Booking confirmed! Waiting for landlord approval.',
        type: 'success'
      });
      return true;
    }
    return false;
  };

  const confirmBooking = async (bookingId: string) => {
    try {
      console.log("confirmBooking started for:", bookingId);
      const booking = bookings.find(b => b.id === bookingId);
      if (!booking) {
        console.error("Booking not found in local state");
        return false;
      }

      // 1. Optimistic Booking Update
      setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status: 'CONFIRMED' } : b));

      // 2. Logic: Handle Room/Property Availability
      const property = properties.find(p => p.id === booking.propertyId);

      if (property) {
        // Deep copy rooms to avoid reference issues
        let updatedRooms = property.rooms ? [...property.rooms] : [];

        if (booking.roomId && updatedRooms.length > 0) {
          // Update specific room or bed availability
          updatedRooms = updatedRooms.map(r => {
            if (r.id === booking.roomId) {
              if (r.pricingType === 'per_bed' && booking.bedId && r.beds) {
                // Shared room: mark specific bed as booked
                const updatedBeds = r.beds.map(b =>
                  b.id === booking.bedId ? { ...b, available: false } : b
                );
                // Room is available if AT LEAST one bed remains
                const anyBedAvailable = updatedBeds.some(b => b.available);
                return { ...r, beds: updatedBeds, available: anyBedAvailable };
              } else {
                // Entire room booking
                return { ...r, available: false };
              }
            }
            return r;
          });

          // Check if *all* rooms in property are now taken
          const allRoomsTaken = updatedRooms.every(r => r.available === false);
          const newStatus = allRoomsTaken ? 'RENTED' : 'AVAILABLE';

          console.log(`Updating rooms. All taken? ${allRoomsTaken}. New Status: ${newStatus}`);

          // Update Property in DB (Rooms + Status)
          const { error: propError } = await updateProperty(property.id, {
            status: newStatus,
            rooms: updatedRooms
          });

          if (propError) console.error("Property update failed (non-critical):", propError);
        } else {
          // Whole unit booking (no rooms defined)
          console.log("Whole unit booking. Setting RENTED.");
          const { error: propError } = await updateProperty(property.id, { status: 'RENTED' });
          if (propError) console.error("Property update failed (non-critical):", propError);
        }
      }

      // 3. DB Update for Booking Status
      const { error } = await supabase.from('bookings')
        .update({ status: 'CONFIRMED' })
        .eq('id', bookingId)
        .select();

      if (error) {
        console.error("Confirm Booking Failed.", error);
        // Revert optimistic update
        setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status: 'PENDING' } : b));
        return false;
      }

      // 4. Notify Student
      addNotification({
        userId: booking.studentId,
        message: 'Your booking has been approved!',
        type: 'success'
      });

      return true;
    } catch (e) {
      console.error("Critical Exception in confirmBooking:", e);
      return false;
    }
  };

  const rejectBooking = async (bookingId: string) => {
    try {
      console.log("rejectBooking started for:", bookingId);
      const booking = bookings.find(b => b.id === bookingId);
      if (!booking) return false;

      // Optimistic Update
      setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status: 'CANCELLED' } : b));

      const { error } = await supabase.from('bookings')
        .update({ status: 'CANCELLED' })
        .eq('id', bookingId)
        .select();

      if (error) {
        console.error("Reject Booking Failed:", error.message, error.details, error.hint);
        // Revert
        setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status: 'PENDING' } : b));
        return false;
      }

      // Notify Student
      addNotification({
        userId: booking.studentId,
        message: 'Your booking request was declined by the agent.',
        type: 'error'
      });

      return true;
    } catch (e) {
      console.error("Critical Exception in rejectBooking:", e);
      return false;
    }
  };

  // --- Notification Actions ---
  const addNotification = async (newNotif: Omit<Notification, 'id' | 'date' | 'read'>) => {
    const { data, error } = await supabase.from('notifications').insert([{
      user_id: newNotif.userId,
      message: newNotif.message,
      type: newNotif.type,
      read: false
    }]).select();

    if (error) {
      console.error("Error adding notification:", error);
      return;
    }

    if (data) {
      const notification: Notification = {
        id: data[0].id,
        userId: data[0].user_id,
        message: data[0].message,
        type: data[0].type,
        date: data[0].created_at,
        read: data[0].read
      };
      setNotifications(prev => [notification, ...prev]);
    }
  };

  const markNotificationRead = async (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    const { error } = await supabase.from('notifications').update({ read: true }).eq('id', id);
    if (error) console.error("Error marking notification as read:", error);
  };

  // --- Message Actions ---
  const sendMessage = async (receiverId: string, content: string, propertyId?: string) => {
    if (!user) return;

    const newMessage = {
      sender_id: user.id,
      receiver_id: receiverId,
      property_id: propertyId,
      content,
      is_read: false
    };

    // Optimistic
    const tempMsg = {
      id: `temp-${Date.now()}`,
      senderId: user.id,
      receiverId,
      propertyId,
      content,
      isRead: false,
      createdAt: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempMsg]);

    const { data } = await supabase.from('messages').insert([newMessage]).select();
    if (data) {
      setMessages(prev => prev.map(m => m.id === tempMsg.id ? { ...m, id: data[0].id, createdAt: data[0].created_at } : m));
    }
  };

  const markMessagesAsRead = async (senderId: string) => {
    if (!user) return;

    // Optimistic Update
    setMessages(prev => prev.map(m =>
      (m.senderId === senderId && m.receiverId === user.id && !m.isRead)
        ? { ...m, isRead: true }
        : m
    ));

    // Async DB Update
    await supabase
      .from('messages')
      .update({ is_read: true })
      .eq('sender_id', senderId)
      .eq('receiver_id', user.id)
      .eq('is_read', false);
  };

  // --- Saved Property Actions ---
  const toggleSavedProperty = async (propertyId: string) => {
    if (!user) return;

    const isSaved = savedProperties.includes(propertyId);
    let newSaved = [...savedProperties];

    if (isSaved) {
      newSaved = newSaved.filter(id => id !== propertyId);
      setSavedProperties(newSaved);
      await supabase.from('saved_properties').delete().match({ user_id: user.id, property_id: propertyId });
    } else {
      newSaved.push(propertyId);
      setSavedProperties(newSaved);
      await supabase.from('saved_properties').insert([{ user_id: user.id, property_id: propertyId }]);
    }
  };

  // --- Payment Actions ---
  const payPayment = async (paymentId: string) => {
    setPayments(prev => prev.map(p => p.id === paymentId ? { ...p, status: 'COMPLETED', paidAt: new Date().toISOString() } : p));
    await supabase.from('payments').update({ status: 'COMPLETED', paid_at: new Date().toISOString() }).eq('id', paymentId);

    addNotification({
      userId: user!.id,
      message: 'Payment successful!',
      type: 'success'
    });
  };

  // --- User Management Actions ---
  const updateUser = async (id: string, updates: Partial<User>) => {
    setUsers(prev => prev.map(u => u.id === id ? { ...u, ...updates } : u));

    // Construct DB update object
    const dbUpdates: any = {
      name: updates.name,
      role: updates.role,
      status: updates.status,
      phone_number: updates.phoneNumber
    };

    // Handle trusted flag specifically
    if (updates.isTrusted !== undefined) {
      dbUpdates.is_trusted = updates.isTrusted;
    }

    // console.log('[DataContext] Updating user:', id, dbUpdates);

    const { error } = await supabase
      .from('profiles')
      .update(dbUpdates)
      .eq('id', id);

    if (error) {
      console.error("[DataContext] Error updating user:", error);
    }
  };

  // --- Landlord Actions ---
  const addLandlord = async (newLandlord: Omit<Landlord, 'id' | 'createdAt' | 'updatedAt'>) => {
    const { data, error } = await supabase.from('landlords').insert([{
      agent_id: newLandlord.agentId,
      full_name: newLandlord.fullName,
      phone_number: newLandlord.phoneNumber,
      email: newLandlord.email,
      physical_address: newLandlord.physicalAddress
    }]).select();

    if (error) {
      console.error("Error adding landlord:", error);
      return { error };
    }

    if (data) {
      fetchAllData();
      logAction('LANDLORD_CREATE', data[0].id, 'landlord', newLandlord);
    }
    return { data: data[0], error: null };
  };

  const updateLandlord = async (id: string, updates: Partial<Landlord>) => {
    const dbUpdates: any = {};
    if (updates.fullName) dbUpdates.full_name = updates.fullName;
    if (updates.phoneNumber) dbUpdates.phone_number = updates.phoneNumber;
    if (updates.email) dbUpdates.email = updates.email;
    if (updates.physicalAddress) dbUpdates.physical_address = updates.physicalAddress;

    const { error } = await supabase.from('landlords').update(dbUpdates).eq('id', id);
    if (!error) {
      fetchAllData();
      logAction('LANDLORD_UPDATE', id, 'landlord', updates);
    }
    return { error };
  };

  const deleteLandlord = async (id: string, hardDelete: boolean = false) => {
    if (hardDelete && user?.role === UserRole.ADMIN) {
      await supabase.from('landlords').delete().eq('id', id);
    } else {
      await supabase.from('landlords').update({ deleted_at: new Date().toISOString() }).eq('id', id);
      logAction('LANDLORD_SOFT_DELETE', id, 'landlord');
    }
    fetchAllData();
  };

  // --- Internal Notes Actions ---
  const addNote = async (newNote: Omit<InternalNote, 'id' | 'createdAt' | 'updatedAt'>) => {
    const { error } = await supabase.from('internal_notes').insert([{
      user_id: user?.id,
      target_id: newNote.targetId,
      target_type: newNote.targetType,
      content: newNote.content
    }]);

    if (!error) {
      fetchAllData();
    }
  };

  const deleteNote = async (id: string) => {
    const { error } = await supabase.from('internal_notes').update({ deleted_at: new Date().toISOString() }).eq('id', id);
    if (!error) fetchAllData();
  };

  // --- Audit Logging ---
  const logAction = async (action: string, targetId: string, targetType: string, payload?: any) => {
    await supabase.from('audit_logs').insert([{
      user_id: user?.id,
      action,
      target_id: targetId,
      target_type: targetType,
      payload
    }]);
  };

  const setBookingStatus = async (id: string, status: Booking['status']) => {
    setBookings(prev => prev.map(b => b.id === id ? { ...b, status } : b));
    const { error } = await supabase.from('bookings').update({ status }).eq('id', id);
    if (error) {
      console.error("Error updating booking status:", error);
      // Revert if needed, but fetchAllData usually handles sync
      fetchAllData();
    } else {
      logAction('BOOKING_STATUS_UPDATE', id, 'booking', { status });
    }
  };

  const updateBooking = async (id: string, updates: Partial<Booking>) => {
    // Optimistic Update
    setBookings(prev => prev.map(b => b.id === id ? { ...b, ...updates } : b));

    const dbUpdates: any = {};
    if (updates.status) dbUpdates.status = updates.status;
    if (updates.roomId) dbUpdates.room_id = updates.roomId;
    if (updates.bedId) dbUpdates.bed_id = updates.bedId;
    if (updates.propertyId) dbUpdates.property_id = updates.propertyId;

    const { error } = await supabase.from('bookings').update(dbUpdates).eq('id', id);

    if (error) {
      console.error("Error updating booking:", error);
      fetchAllData();
      return false;
    }

    logAction('BOOKING_UPDATE', id, 'booking', updates);
    return true;
  };

  const reassignPropertyAgent = async (propertyId: string, agentId: string) => {
    // Optimistic Update
    setProperties(prev => prev.map(p => p.id === propertyId ? { ...p, agentId } : p));

    const { error } = await supabase.from('properties').update({ agent_id: agentId }).eq('id', propertyId);

    if (error) {
      console.error("Error reassigning agent:", error);
      fetchAllData();
      return false;
    }

    logAction('PROPERTY_AGENT_REASSIGN', propertyId, 'property', { agentId });
    return true;
  };

  const deleteUser = async (id: string, hardDelete: boolean = false) => {
    if (hardDelete && user?.role === UserRole.ADMIN) {
      setUsers(prev => prev.filter(u => u.id !== id));
      const { error } = await supabase.from('profiles').delete().eq('id', id);
      return { error };
    } else {
      const deletionData = {
        deleted_at: new Date().toISOString(),
        deleted_by: user?.id
      };
      setUsers(prev => prev.map(u => u.id === id ? { ...u, deletedAt: deletionData.deleted_at, deletedBy: deletionData.deleted_by } : u));
      const { error } = await supabase.from('profiles').update(deletionData).eq('id', id);

      logAction('USER_SOFT_DELETE', id, 'user');
      return { error };
    }
  };

  // --- Referral Actions ---
  const addReferralCampaign = async (campaign: Omit<ReferralCampaign, 'id'>) => {
    // Optimistic Update
    const tempId = `rc-${Date.now()}`;
    const newCampaign = { ...campaign, id: tempId };
    setReferralCampaigns(prev => [newCampaign, ...prev]);

    const { data, error } = await supabase.from('referral_campaigns').insert([{
      name: campaign.name,
      benefit: campaign.benefit,
      reward_amount: campaign.rewardAmount,
      threshold_amount: campaign.thresholdAmount,
      is_active: campaign.isActive,
      start_date: campaign.startDate
    }]).select();

    if (error) {
      console.error("Failed to add campaign:", error);
      // Rollback
      setReferralCampaigns(prev => prev.filter(c => c.id !== tempId));
    } else if (data) {
      // Update ID
      setReferralCampaigns(prev => prev.map(c => c.id === tempId ? { ...c, id: data[0].id } : c));
    }
  };

  const toggleReferralCampaign = async (id: string) => {
    const campaign = referralCampaigns.find(c => c.id === id);
    if (!campaign) return;

    const newStatus = !campaign.isActive;

    // Optimistic
    setReferralCampaigns(prev => prev.map(c => c.id === id ? { ...c, isActive: newStatus } : c));

    const { error } = await supabase.from('referral_campaigns').update({ is_active: newStatus }).eq('id', id);

    if (error) {
      console.error("Failed to toggle campaign:", error);
      // Rollback
      setReferralCampaigns(prev => prev.map(c => c.id === id ? { ...c, isActive: !newStatus } : c));
    }
  };

  const validateReferralCode = (code: string): User | undefined => {
    if (!code) return undefined;
    return users.find(u => u.referralCode === code.toUpperCase());
  };

  const requestWithdrawal = async (details: Omit<WithdrawalRequest, 'id' | 'status' | 'createdAt'>) => {
    // Optimistic user balance update
    setUsers(prev => prev.map(u => u.id === details.userId ? { ...u, referralBalance: (u.referralBalance || 0) - details.amount } : u));

    const { data, error } = await supabase.from('withdrawal_requests').insert([{
      user_id: details.userId,
      amount: details.amount,
      bank_name: details.bankName,
      account_number: details.accountNumber,
      account_holder_name: details.accountHolderName,
      branch_code: details.branchCode,
      id_number: details.idNumber,
      email: details.email,
      status: 'PENDING'
    }]).select();

    if (error) {
      console.error("Withdrawal Request Failed:", error);
      // Revert Balance
      setUsers(prev => prev.map(u => u.id === details.userId ? { ...u, referralBalance: (u.referralBalance || 0) + details.amount } : u));
      return false;
    }

    if (data) {
      // Also deduct balance in DB
      await supabase.rpc('deduct_referral_balance', { user_id: details.userId, amount: details.amount });

      setWithdrawalRequests(prev => [{
        id: data[0].id,
        userId: details.userId,
        amount: details.amount,
        status: 'PENDING',
        createdAt: data[0].created_at,
        bankName: details.bankName,
        accountNumber: details.accountNumber,
        accountHolderName: details.accountHolderName,
        branchCode: details.branchCode,
        idNumber: details.idNumber,
        email: details.email
      }, ...prev]);
      return true;
    }
    return false;
  };

  const updateWithdrawalStatus = async (id: string, status: 'PAID' | 'REJECTED') => {
    setWithdrawalRequests(prev => prev.map(r => r.id === id ? { ...r, status } : r));
    await supabase.from('withdrawal_requests').update({ status }).eq('id', id);

    // If rejected, refund balance (simplified logic)
    if (status === 'REJECTED') {
      const request = withdrawalRequests.find(r => r.id === id);
      if (request) {
        // Logic to increment balance back would go here
      }
    }
  };

  // --- Form Actions ---
  const addApplicationForm = async (form: Omit<ApplicationForm, 'id' | 'createdAt'>) => {
    // Optimistic
    const tempId = `form-${Date.now()}`;
    const newForm: ApplicationForm = { ...form, id: tempId, createdAt: new Date().toISOString() };
    setApplicationForms(prev => [newForm, ...prev]);

    const { data, error } = await supabase.from('application_forms').insert([{
      agent_id: form.agentId,
      title: form.title,
      type: form.type,
      description: form.description,
      is_active: form.isActive,
      fields: form.fields
    }]).select();

    if (error) {
      console.error("Add Form Error:", error);
      setApplicationForms(prev => prev.filter(f => f.id !== tempId));
      return false;
    }

    if (data) {
      setApplicationForms(prev => prev.map(f => f.id === tempId ? { ...f, id: data[0].id } : f));
      return true;
    }
    return false;
  };

  const updateApplicationForm = async (id: string, updates: Partial<ApplicationForm>) => {
    // Optimistic
    setApplicationForms(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f));

    const payload: any = {};
    if (updates.title) payload.title = updates.title;
    if (updates.description) payload.description = updates.description;
    if (updates.fields) payload.fields = updates.fields;
    if (updates.isActive !== undefined) payload.is_active = updates.isActive;

    const { error } = await supabase.from('application_forms').update(payload).eq('id', id);

    if (error) {
      console.error("Update Form Error:", error);
      return false;
    }
    return true;
  };

  const toggleApplicationForm = async (id: string) => {
    const form = applicationForms.find(f => f.id === id);
    if (!form) return;

    const newStatus = !form.isActive;
    setApplicationForms(prev => prev.map(f => f.id === id ? { ...f, isActive: newStatus } : f));

    const { error } = await supabase.from('application_forms').update({ is_active: newStatus }).eq('id', id);
    if (error) {
      console.error("Toggle Form Error:", error);
      setApplicationForms(prev => prev.map(f => f.id === id ? { ...f, isActive: !newStatus } : f));
    }
  };

  return (
    <DataContext.Provider value={{
      properties,
      bookings,
      bookingsError,
      notifications,
      payments,
      messages,
      savedProperties,
      users,
      referralCampaigns,
      withdrawalRequests,
      landlords,
      auditLogs,
      internalNotes,
      addProperty,
      updateProperty,
      deleteProperty,
      addBooking,
      confirmBooking,
      rejectBooking,
      setBookingStatus,
      updateBooking,
      reassignPropertyAgent,
      addNotification,
      markNotificationRead,
      sendMessage,
      markMessagesAsRead,
      toggleSavedProperty,
      payPayment,
      updateUser,
      deleteUser,
      addLandlord,
      updateLandlord,
      deleteLandlord,
      addNote,
      deleteNote,
      logAction,
      refreshData,
      getUserLastActivity,
      getUserSubscription,
      addReferralCampaign,
      toggleReferralCampaign,
      validateReferralCode,
      requestWithdrawal,
      updateWithdrawalStatus,
      applicationForms,
      addApplicationForm,
      updateApplicationForm,
      toggleApplicationForm
    }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};